import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useForm, Controller } from 'react-hook-form';
import { X, MapPin, Package, Calendar, DollarSign, Info, User } from 'lucide-react';
import toast from 'react-hot-toast';
import { parcelsAPI } from '../services/api';
import { useThemeColors } from '../theme/ThemeContext';
import { feesAPI } from '../services/api';
import Select from 'react-select';
import { Country, State, City } from "country-state-city";

// Get countries from package
const countries = Country.getAllCountries();

// Get cities for selected country
const getCountryCities = (countryCode) => {
  if (!countryCode) return [];
  
  try {
    console.log(`Getting cities for country: ${countryCode}`);
    
    // Get all states for the country
    const states = State.getStatesOfCountry(countryCode);
    console.log(`States for ${countryCode}:`, states);
    
    if (!states || states.length === 0) {
      console.log(`No states found for ${countryCode}`);
      return [];
    }
    
    // Get cities from all states
    let allCities = [];
    states.forEach(state => {
      try {
        const cities = City.getCitiesOfState(countryCode, state.isoCode);
        if (cities && cities.length > 0) {
          allCities = allCities.concat(cities);
        }
      } catch (error) {
        console.log(`Error getting cities for state ${state.isoCode}:`, error);
      }
    });
    
    // Extract city names and remove duplicates
    const cityNames = allCities.map(city => city.name);
    const uniqueCityNames = [...new Set(cityNames)];
    console.log(`Final cities for ${countryCode}:`, uniqueCityNames.slice(0, 20)); // Show first 20
    return uniqueCityNames.sort();
  } catch (error) {
    console.log(`Error getting cities for country ${countryCode}:`, error);
    return [];
  }
};

const CreateParcelModal = ({ isOpen, isLoading, onClose, onSubmit, repostData }) => {
  const colors = useThemeColors();
  const { register, handleSubmit, watch, setValue, control, formState: { errors } } = useForm({
    defaultValues: {
      deliveryType: 'standard' // Default to standard delivery
    }
  });
  const [feeEstimate, setFeeEstimate] = useState(null);
  const [isCalculatingFee, setIsCalculatingFee] = useState(false);
  const [selectedPickupCountry, setSelectedPickupCountry] = useState('');
  const [selectedDropoffCountry, setSelectedDropoffCountry] = useState('');
  const [pickupCities, setPickupCities] = useState([]);
  const [dropoffCities, setDropoffCities] = useState([]);

  // Simple country list without external dependencies
  const countryList = [
    { value: 'Ethiopia', label: 'Ethiopia', cities: ['Addis Ababa', 'Dire Dawa', 'Mekelle', 'Bahir Dar', 'Gondar', 'Hawassa'] },
    { value: 'Kenya', label: 'Kenya', cities: ['Nairobi', 'Mombasa', 'Kisumu', 'Eldoret', 'Nakuru'] },
    { value: 'Uganda', label: 'Uganda', cities: ['Kampala', 'Entebbe', 'Jinja', 'Gulu', 'Mbarara'] },
    { value: 'Tanzania', label: 'Tanzania', cities: ['Dar es Salaam', 'Mwanza', 'Arusha', 'Dodoma', 'Tanga'] },
    { value: 'Sudan', label: 'Sudan', cities: ['Khartoum', 'Omdurman', 'Port Sudan', 'Al Qadarif', 'Nyala'] },
    { value: 'USA', label: 'United States', cities: ['New York', 'Los Angeles', 'Chicago', 'Houston', 'Miami', 'Seattle'] },
    { value: 'UK', label: 'United Kingdom', cities: ['London', 'Manchester', 'Birmingham', 'Liverpool', 'Glasgow'] },
    { value: 'France', label: 'France', cities: ['Paris', 'Marseille', 'Lyon', 'Toulouse', 'Nice'] },
    { value: 'Germany', label: 'Germany', cities: ['Berlin', 'Munich', 'Hamburg', 'Frankfurt', 'Cologne'] },
    { value: 'Netherlands', label: 'Netherlands', cities: ['Amsterdam', 'Rotterdam', 'The Hague', 'Utrecht', 'Eindhoven'] }
  ];

  // Handlers for country selection
  const handlePickupCountryChange = (country) => {
    setSelectedPickupCountry(country);
    setValue('pickupCountry', country);
    // Clear city when country changes
    setValue('pickupCity', '');
    // Load cities for selected country
    if (country) {
      const countryCode = countries.find(c => c.name === country)?.isoCode;
      if (countryCode) {
        const cities = getCountryCities(countryCode);
        setPickupCities(cities);
      }
    } else {
      setPickupCities([]);
    }
  };

  const handleDropoffCountryChange = (country) => {
    setSelectedDropoffCountry(country);
    setValue('dropoffCountry', country);
    // Clear city when country changes
    setValue('dropoffCity', '');
    // Load cities for selected country
    if (country) {
      const countryCode = countries.find(c => c.name === country)?.isoCode;
      if (countryCode) {
        const cities = getCountryCities(countryCode);
        setDropoffCities(cities);
      }
    } else {
      setDropoffCities([]);
    }
  };

  const watchedValues = watch();
  
  // Check for repost data and pre-fill form
  useEffect(() => {
    console.log('Checking for repost data...', repostData);
    
    if (repostData) {
      console.log('Loading repost data:', repostData);
      
      // Pre-fill all form fields
      Object.keys(repostData).forEach(key => {
        if (repostData[key] !== undefined && repostData[key] !== null) {
          console.log(`Setting ${key} to:`, repostData[key]);
          setValue(key, repostData[key]);
        }
      });
      
      toast.success('Parcel details loaded for reposting!');
    }
  }, [repostData, setValue]);
  
  // // Debug: Log form values to see what's happening
  // useEffect(() => {
  //   console.log('Form values:', {
  //     ...watchedValues,
  //     pickupCountry: watchedValues.pickupCountry,
  //     dropoffCountry: watchedValues.dropoffCountry
  //   });
  //   console.log('Selected pickup country:', selectedPickupCountry);
  //   console.log('Selected dropoff country:', selectedDropoffCountry);
  // }, [watchedValues, selectedPickupCountry, selectedDropoffCountry]);

  // Calculate AI suggested price based on parcel details
  const calculateSuggestedPrice = () => {
    if (!watchedValues.weight_kg || (!watchedValues.declared_value && !watchedValues.purchase_price)) {
      return 0;
    }

    const weight = parseFloat(watchedValues.weight_kg);
    const value = parseFloat(watchedValues.declared_value) || parseFloat(watchedValues.purchase_price) || 0;
    
    // Base pricing calculation
    let basePrice = 100; // Base delivery fee
    
    // Weight-based pricing
    if (weight <= 1) basePrice += 50;
    else if (weight <= 5) basePrice += 100;
    else if (weight <= 10) basePrice += 200;
    else basePrice += 350;
    
    // Value-based multiplier
    if (value > 0) {
      basePrice += value * 0.05; // 5% of declared value
    }
    
    // Distance simulation (simplified - would use real distance API)
    const distanceMultiplier = 1.2; // Assume medium distance
    
    // Market demand factor (simplified)
    const demandFactor = 1.1; // Slight demand premium
    
    return Math.round(basePrice * distanceMultiplier * demandFactor);
  };

  // Calculate fee estimate when relevant fields change
  useEffect(() => {
    const calculateFee = async () => {
      if (watchedValues.weight_kg || watchedValues.declared_value || watchedValues.purchase_price) {
        setIsCalculatingFee(true);
        try {
          const response = await feesAPI.calculateFeeEstimate('post_parcel', {
            weight_kg: parseFloat(watchedValues.weight_kg),
            declared_value: parseFloat(watchedValues.declared_value) || 0,
            purchase_price: parseFloat(watchedValues.purchase_price) || 0,
            guaranteed_delivery: watchedValues.guaranteedDelivery || false,
            purchased: watchedValues.purchased || false,
            max_delivery_price: parseFloat(watchedValues.max_delivery_price)
          });
          setFeeEstimate(response.data.data);
        } catch (error) {
          console.error('Failed to calculate fee:', error);
        } finally {
          setIsCalculatingFee(false);
        }
      }
    };

    const timeoutId = setTimeout(calculateFee, 500);
    return () => clearTimeout(timeoutId);
  }, [watchedValues.weight_kg, watchedValues.declared_value, watchedValues.purchase_price, watchedValues.max_delivery_price, watchedValues.guaranteedDelivery, watchedValues.purchased]);

  const onFormSubmit = (data) => {
    const parcelData = {
      pickup_city: data.pickupCity.value,
      pickup_country: data.pickupCountry,
      dropoff_city: data.dropoffCity.value,
      dropoff_country: data.dropoffCountry,
      weight_kg: parseFloat(data.weight_kg),
      category: data.category,
      description: data.description,
      expires_at: data.deadline,
      // Handle checkbox logic
      guaranteed_delivery: data.guaranteedDelivery || false,
      purchased: data.purchased || false,
      declared_value: data.guaranteedDelivery ? parseFloat(data.declared_value) || 0 : null,
      purchase_price: data.purchased ? parseFloat(data.purchase_price) : null,
      max_delivery_price: parseFloat(data.max_delivery_price),
      receiver_name: data.receiverName,
      receiver_phone: data.receiverPhone
    };
    console.log(parcelData);

    onSubmit(parcelData);
  };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-theme-card rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold text-theme-primary">Create New Parcel</h2>
          <button
            onClick={onClose}
            className="text-theme-tertiary hover:text-theme-secondary"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onFormSubmit)} className="p-6 space-y-6">
          {/* Pickup Location */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-theme-primary text-theme-primary flex items-center">
              <MapPin className="h-5 w-5 mr-2 text-theme-primary" />
              Pickup Location
            </h3>

            <div>
              <label className="block text-sm font-medium text-theme-primary text-theme-secondary mb-1">
                Pickup Country *
              </label>
              <Controller
                name="pickupCountry"
                control={control}
                rules={{ required: 'Pickup country is required' }}
                render={({ field, fieldState: { error } }) => (
                  <>
                    <Select
                      {...field}
                      options={countries.map(country => ({
                        value: country.name,
                        label: country.name,
                        isoCode: country.isoCode
                      }))}
                      placeholder="Select country"
                      className="w-full"
                      classNamePrefix="react-select"
                      onChange={(selectedOption) => {
                        const value = selectedOption?.value || '';
                        field.onChange(value);
                        setSelectedPickupCountry(value);
                        setValue('pickupCity', '');
                        // Load cities for selected country
                        if (value) {
                          const countryCode = countries.find(c => c.name === value)?.isoCode;
                          if (countryCode) {
                            const cities = getCountryCities(countryCode);
                            setPickupCities(cities);
                          }
                        } else {
                          setPickupCities([]);
                        }
                      }}
                      value={field.value ? { value: field.value, label: field.value } : null}
                      styles={{
                        control: (baseStyles, state) => ({
                          ...baseStyles,
                          borderColor: state.isFocused ? colors.primary[500] : '#e5e7eb',
                          '&:hover': { borderColor: colors.primary[400] },
                          boxShadow: state.isFocused ? `0 0 0 1px ${colors.primary[500]}` : 'none',
                          borderRadius: '0.375rem',
                          padding: '0.5rem 0.75rem'
                        })
                      }}
                    />
                    {error && (
                      <p className="mt-1 text-sm text-theme-error">{error.message}</p>
                    )}
                  </>
                )}
              />
            </div>            
            <div>
              <label className="block text-sm font-medium text-theme-primary text-theme-secondary mb-1">
                Pickup City *
              </label>
              {selectedPickupCountry && pickupCities.length > 0 ? (
                <Controller
                  name="pickupCity"
                  control={control}
                  rules={{ required: 'Pickup city is required' }}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <Select
                        {...field}
                        options={pickupCities.map(city => ({
                          value: city,
                          label: city
                        }))}
                        placeholder="Select city"
                        className="w-full"
                        classNamePrefix="react-select"
                        styles={{
                          control: (baseStyles, state) => ({
                            ...baseStyles,
                            borderColor: state.isFocused ? colors.primary[500] : '#e5e7eb',
                            '&:hover': { borderColor: colors.primary[400] },
                            boxShadow: state.isFocused ? `0 0 0 1px ${colors.primary[500]}` : 'none',
                            borderRadius: '0.375rem',
                            padding: '0.5rem 0.75rem'
                          })
                        }}
                      />
                      {error && (
                        <p className="mt-1 text-sm text-theme-error">{error.message}</p>
                      )}
                    </>
                  )}
                />
              ) : (
                <input
                  {...register('pickupCity', { required: 'Pickup city is required' })}
                  type="text"
                  className="w-full px-3 py-2 border border-theme-light rounded-md focus:ring-theme-primary focus:border-theme-primary"
                  placeholder="Enter city manually"
                />
              )}
              {errors.pickupCity && !selectedPickupCountry && (
                <p className="mt-1 text-sm text-theme-error">{errors.pickupCity.message}</p>
              )}
            </div>
            
          </div>

          {/* Dropoff Location */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-theme-primary text-theme-primary flex items-center">
              <MapPin className="h-5 w-5 mr-2 text-theme-success" />
              Dropoff Location
            </h3>
            
            <div>
              <label className="block text-sm font-medium text-theme-primary text-theme-secondary mb-1">
                Dropoff Country *
              </label>
              <Controller
                name="dropoffCountry"
                control={control}
                rules={{ required: 'Dropoff country is required' }}
                render={({ field, fieldState: { error } }) => (
                  <>
                    <Select
                      {...field}
                      options={countries.map(country => ({
                        value: country.name,
                        label: country.name,
                        isoCode: country.isoCode
                      }))}
                      placeholder="Select country"
                      className="w-full"
                      classNamePrefix="react-select"
                      onChange={(selectedOption) => {
                        const value = selectedOption?.value || '';
                        field.onChange(value);
                        setSelectedDropoffCountry(value);
                        setValue('dropoffCity', '');
                        // Load cities for selected country
                        if (value) {
                          const countryCode = countries.find(c => c.name === value)?.isoCode;
                          if (countryCode) {
                            const cities = getCountryCities(countryCode);
                            setDropoffCities(cities);
                          }
                        } else {
                          setDropoffCities([]);
                        }
                      }}
                      value={field.value ? { value: field.value, label: field.value } : null}
                      styles={{
                        control: (baseStyles, state) => ({
                          ...baseStyles,
                          borderColor: state.isFocused ? colors.primary[500] : '#e5e7eb',
                          '&:hover': { borderColor: colors.primary[400] },
                          boxShadow: state.isFocused ? `0 0 0 1px ${colors.primary[500]}` : 'none',
                          borderRadius: '0.375rem',
                          padding: '0.5rem 0.75rem'
                        })
                      }}
                    />
                    {error && (
                      <p className="mt-1 text-sm text-theme-error">{error.message}</p>
                    )}
                  </>
                )}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-theme-primary text-theme-secondary mb-1">
                Dropoff City *
              </label>
              {selectedDropoffCountry && dropoffCities.length > 0 ? (
                <Controller
                  name="dropoffCity"
                  control={control}
                  rules={{ required: 'Dropoff city is required' }}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <Select
                        {...field}
                        options={dropoffCities.map(city => ({
                          value: city,
                          label: city
                        }))}
                        placeholder="Select city"
                        className="w-full"
                        classNamePrefix="react-select"
                        styles={{
                          control: (baseStyles, state) => ({
                            ...baseStyles,
                            borderColor: state.isFocused ? colors.primary[500] : '#e5e7eb',
                            '&:hover': { borderColor: colors.primary[400] },
                            boxShadow: state.isFocused ? `0 0 0 1px ${colors.primary[500]}` : 'none',
                            borderRadius: '0.375rem',
                            padding: '0.5rem 0.75rem'
                          })
                        }}
                      />
                      {error && (
                        <p className="mt-1 text-sm text-theme-error">{error.message}</p>
                      )}
                    </>
                  )}
                />
              ) : (
                <input
                  {...register('dropoffCity', { required: 'Dropoff city is required' })}
                  type="text"
                  className="w-full px-3 py-2 border border-theme-light rounded-md focus:ring-theme-primary focus:border-theme-primary"
                  placeholder="Enter city manually"
                />
              )}
              {errors.dropoffCity && !selectedDropoffCountry && (
                <p className="mt-1 text-sm text-theme-error">{errors.dropoffCity.message}</p>
              )}
            </div>
          </div>

          {/* Parcel Details */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-theme-primary text-theme-primary flex items-center">
              <Package className="h-5 w-5 mr-2 text-theme-primary" />
              Parcel Details
            </h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-theme-primary text-theme-secondary mb-1">
                  Weight (kg) *
                </label>
                <input
                  {...register('weight_kg', {
                    required: 'Weight is required',
                    min: { value: 0.1, message: 'Weight must be at least 0.1 kg' },
                    max: { value: 50, message: 'Weight cannot exceed 50 kg' },
                  })}
                  type="number"
                  step="0.1"
                  className="w-full px-3 py-2 border border-theme-light rounded-md focus:ring-theme-primary focus:border-theme-primary"
                  placeholder="e.g., 2.5"
                />
                {errors.weight_kg && (
                  <p className="mt-1 text-sm text-theme-error">{errors.weight_kg.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-theme-primary text-theme-secondary mb-1">
                  Category *
                </label>
                <select
                  {...register('category', { required: 'Category is required' })}
                  className="w-full px-3 py-2 border border-theme-light rounded-md focus:ring-theme-primary focus:border-theme-primary"
                >
                  <option value="">Select category</option>
                  <option value="documents">Documents</option>
                  <option value="electronics">Electronics</option>
                  <option value="clothing">Clothing</option>
                  <option value="food">Food</option>
                  <option value="other">Other</option>
                </select>
                {errors.category && (
                  <p className="mt-1 text-sm text-theme-error">{errors.category.message}</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-theme-primary text-theme-secondary mb-1">
                Description
              </label>
              <textarea
                {...register('description')}
                rows={3}
                className="w-full px-3 py-2 border border-theme-light rounded-md focus:ring-theme-primary focus:border-theme-primary"
                placeholder="Describe your parcel contents..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-theme-primary text-theme-secondary mb-1">
                <Calendar className="inline h-4 w-4 mr-1" />
                Best Before Date *
              </label>
              <input
                {...register('deadline', { required: 'Best before date is required' })}
                type="date"
                min={new Date().toISOString().split('T')[0]}
                                className="w-full px-3 py-2 border border-theme-light rounded-md focus:ring-theme-primary focus:border-theme-primary"
              />
              {errors.deadline && (
                <p className="mt-1 text-sm text-theme-error">{errors.deadline.message}</p>
              )}
            </div>
          </div>

          {/* Receiver Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-theme-primary text-theme-primary flex items-center">
              <User className="h-5 w-5 mr-2 text-theme-primary" />
              Receiver Information
            </h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-theme-primary text-theme-secondary mb-1">
                  Receiver Name *
                </label>
                <input
                  {...register('receiverName', { required: 'Receiver name is required' })}
                  type="text"
                  className="w-full px-3 py-2 border border-theme-light rounded-md focus:ring-theme-primary focus:border-theme-primary"
                  placeholder="Full name"
                />
                {errors.receiverName && (
                  <p className="mt-1 text-sm text-theme-error">{errors.receiverName.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-theme-primary text-theme-secondary mb-1">
                  Receiver Phone *
                </label>
                <input
                  {...register('receiverPhone', { 
                    required: 'Receiver phone is required',
                    pattern: {
                      value: /^[+]?[\d\s-()]+$/,
                      message: 'Invalid phone number',
                    }
                  })}
                  type="tel"
                  className="w-full px-3 py-2 border border-theme-light rounded-md focus:ring-theme-primary focus:border-theme-primary"
                  placeholder="+251 911 234 567"
                />
                {errors.receiverPhone && (
                  <p className="mt-1 text-sm text-theme-error">{errors.receiverPhone.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* Delivery Options */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-theme-primary text-theme-primary flex items-center">
              <Calendar className="h-5 w-5 mr-2 text-theme-primary" />
              Delivery Options
            </h3>
            
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-theme-primary text-theme-secondary mb-2">
                  Parcel Type
                </label>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <input
                      {...register('guaranteedDelivery')}
                      type="checkbox"
                      className="h-4 w-4 text-theme-primary focus:ring-blue-500 border-theme-light"
                    />
                    <label className="ml-2 text-sm text-theme-secondary">
                      Guaranteed Delivery (Additional fee applies)
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      {...register('purchased')}
                      type="checkbox"
                      className="h-4 w-4 text-theme-primary focus:ring-blue-500 border-theme-light"
                    />
                    <label className="ml-2 text-sm text-theme-secondary">
                      Purchased Item
                    </label>
                  </div>
                </div>
              </div>

              {watch('guaranteedDelivery') && (
                <div>
                  <label className="block text-sm font-medium text-theme-primary text-theme-secondary mb-1">
                    Declared Value (ETB)
                    <span className="text-xs text-theme-muted ml-1">(Value of items being delivered)</span>
                  </label>
                  <input
                    {...register('declared_value', {
                      required: 'Declared value is required',
                      min: { value: 1, message: 'Value cannot be less than 1' }
                    })}
                    type="number"
                    step="0.01"
                    className="w-full px-3 py-2 border border-theme-light rounded-md focus:ring-theme-primary focus:border-theme-primary"
                    placeholder="e.g., 1000.00"
                  />
                  {errors.declared_value && (
                    <p className="mt-1 text-sm text-theme-error">{errors.declared_value.message}</p>
                  )}
                </div>
              )}

              {watch('purchased') && (
                <div>
                  <label className="block text-sm font-medium text-theme-primary text-theme-secondary mb-1">
                    Purchase Price (ETB)
                    <span className="text-xs text-theme-muted ml-1">(Cost of purchased items)</span>
                  </label>
                  <input
                    {...register('purchase_price', {
                      required: 'Purchase price is required',
                      min: { value: 1, message: 'Value cannot be less than 1' }
                    })}
                    type="number"
                    step="0.01"
                    className="w-full px-3 py-2 border border-theme-light rounded-md focus:ring-theme-primary focus:border-theme-primary"
                    placeholder="e.g., 500.00"
                  />
                  {errors.purchase_price && (
                    <p className="mt-1 text-sm text-theme-error">{errors.purchase_price.message}</p>
                  )}
                </div>
              )}

              {/* Delivery Pricing */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-theme-primary flex items-center">
                  <DollarSign className="h-5 w-5 mr-2 text-theme-primary" />
                  Delivery Pricing
                </h3>
                
                <div className="space-y-4">
                  {/* Maximum Delivery Price */}
                  <div>
                    <label className="block text-sm font-medium text-theme-primary text-theme-secondary mb-1">
                      Maximum Delivery Price (ETB) *
                    </label>
                    <div className="relative">
                      <input
                        {...register('max_delivery_price', {
                          required: 'Maximum delivery price is required',
                          min: { value: 1, message: 'Minimum price is ETB 1' }
                        })}
                        type="number"
                        step="1"
                        className="w-full px-3 py-2 pr-16 border border-theme-light rounded-md focus:ring-theme-primary focus:border-theme-primary"
                        placeholder="Maximum amount you're willing to pay"
                      />
                      <div className="absolute right-3 top-2.5 flex items-center text-sm text-theme-secondary">
                        <DollarSign className="h-4 w-4" />
                      </div>
                    </div>
                    {errors.max_delivery_price && (
                      <p className="mt-1 text-sm text-theme-error">{errors.max_delivery_price.message}</p>
                    )}
                    <p className="text-xs text-theme-muted mt-1">
                      Applicants can propose prices up to this maximum
                    </p>
                  </div>

                  {/* AI Price Suggestion */}
                  {watchedValues.weight_kg && (watchedValues.declared_value || watchedValues.purchase_price) && (
                    <div className="bg-theme-info-50 border border-theme-info-200 rounded-lg p-4">
                      <div className="flex items-start">
                        <Info className="h-5 w-5 text-theme-info mt-0.5 mr-2 flex-shrink-0" />
                        <div className="flex-1">
                          <h4 className="text-sm font-medium text-theme-primary mb-2">
                            💡 AI Price Suggestion
                          </h4>
                          <div className="space-y-1 text-sm">
                            <div className="flex justify-between items-center">
                              <span className="text-theme-secondary">Suggested Price:</span>
                              <span className="font-medium text-theme-primary">
                                ETB {calculateSuggestedPrice()}
                              </span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-theme-secondary">Price Range:</span>
                              <span className="font-medium text-theme-primary">
                                ETB {Math.round(calculateSuggestedPrice() * 0.8)} - {Math.round(calculateSuggestedPrice() * 1.2)}
                              </span>
                            </div>
                            <p className="text-xs text-theme-secondary mt-2">
                              Based on route distance, weight, and current market demand
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Pricing Model Info */}
                  <div className="bg-theme-neutral-50 rounded-lg p-4 border border-theme-neutral-200">
                    <h4 className="text-sm font-medium text-theme-primary mb-2">
                      🎯 How Pricing Works
                    </h4>
                    <div className="space-y-2 text-sm text-theme-secondary">
                      <div className="flex items-center">
                        <div className="w-2 h-2 bg-theme-success rounded-full mr-2"></div>
                        <span>Applicants compete by offering their best price</span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-2 h-2 bg-theme-primary rounded-full mr-2"></div>
                        <span>You choose the best offer from all applicants</span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-2 h-2 bg-theme-warning rounded-full mr-2"></div>
                        <span>Maximum price protects your budget</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {!watch('guaranteedDelivery') && !watch('purchased') && (
                <div className="text-sm text-theme-muted p-4 bg-theme-neutral-50 rounded-md border border-theme-neutral-200">
                  <p>Standard delivery selected - No additional information needed</p>
                </div>
              )}
            </div>
          </div>

          {/* Fee Estimate */}
          {feeEstimate && (
            <div className="bg-theme-primary-100 border border-theme-primary-200 rounded-md p-4">
              <div className="flex items-start">
                <DollarSign className="h-5 w-5 text-theme-primary mt-0.5 mr-2" />
                <div>
                  <h4 className="text-sm font-medium text-theme-primary text-theme-primary">Fee Estimate</h4>
                  <div className="mt-1 text-sm text-theme-secondary">
                    <div>Posting Fee: {feeEstimate.posting_fee || 0} ETB</div>
                    {/* {feeEstimate.deposit_requirement > 0 && ( */}
                    <div>Security Deposit: {feeEstimate.deposit_requirement || 0} ETB</div>
                    <div>Delivery Fee: {feeEstimate.delivery_fee || 0} ETB</div>
                    {/* )} */}
                    <div className="font-medium text-theme-primary mt-1">
                      Total: {feeEstimate.total_cost || 0} ETB
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Info Note */}
          <div className="bg-theme-neutral-100 border border-theme-light rounded-md p-4">
            <div className="flex items-start">
              <Info className="h-5 w-5 text-theme-secondary mt-0.5 mr-2" />
              <div className="text-sm text-theme-secondary">
                <p className="font-medium text-theme-primary">Important Information:</p>
                <ul className="mt-1 list-disc list-inside space-y-1">
                  <li>Posting fee is non-refundable</li>
                  <li>Security deposit will be refunded upon successful delivery</li>
                  <li>You can cancel the parcel before any applications are received</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-4 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-theme-secondary border border-theme-light rounded-md hover:bg-theme-neutral-100"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-theme-primary text-theme-inverse hover:bg-theme-primary-hover disabled:opacity-50"
              disabled={isLoading || isCalculatingFee}
            >
              {isLoading ? 'Creating...' : 'Create Parcel'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateParcelModal;
